import { Form } from 'formik';
import { FormikDebounce } from '../common/form/FormikDebounce';
import { FC, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { useAppDispatch, useAppSelector } from '../../store';
import { getTraining, selectTraining, selectTrainingState, trainingActions } from '../../store/entities/training';
import { selectIdentityCompanyId } from '../../store/entities/identity';
import { SubmitButton } from '../common/form/SubmitButton';
import { Input } from '../common/form/Input';
import { trainingListActions } from '../../store/entities/trainingList';
import { Select } from '../common/form/Select';
import { QcsMenuItem } from '../common/basic/QcsMenuItem';
import { DetailHeader } from '../common/DetailHeader';
import { FormContainer } from '../common/form/FormContainer';
import * as Yup from 'yup';
import { validations } from '../../utils/validations';
import { TrainingVersion } from './TrainingVersion';
import { WorkplaceAutocomplete } from '../common/form/WorkplaceAutocomplete';
import { FormGroup } from '../common/form/FormGroup';
import { QuestionnaireAutocomplete } from '../common/form/QuestionnaireAutocomplete';
import { DatePicker } from '../common/form/DatePicker';
import { getDateForApi } from '../../utils/date';
import { getCompanyChecks, selectCompanyChecksCanCreateTraining, selectCompanyChecksState } from '../../store/entities/companyChecks';
import { fixNameInitValue, getLangNameObj, joinStrings2 } from '../../utils/format';
import { InputTextWithLang } from '../common/form/InputTextWithLang';
import { PathKey, useNavigation } from '../../utils/urlPaths';
import { selectCompanyCustomization } from '../../store/entities/companyCustomization';
import { EntityState } from '@api/models/enums/EntityState';
import { TrainingDto } from '@api/models/training/TrainingDto';
import { TrainingVersionDto } from '@api/models/training/TrainingVersionDto';
import { trainingService, trainingVersionService } from '@api/services/services';
import { ShowError } from '../ShowError';
import { resources } from '@api/resources';
import { ShowResourceContent } from '../ShowResourceContent';
import { ChangeHistoryTabs } from '../common/changeHistory/ChangeHistoryTabs';

type Validator = {
  name?: Yup.StringSchema<string, Yup.AnyObject, undefined, ''>;
  nameEn?: Yup.StringSchema<string, Yup.AnyObject, undefined, ''>;
  nameRu?: Yup.StringSchema<string, Yup.AnyObject, undefined, ''>;
  nameDe?: Yup.StringSchema<string, Yup.AnyObject, undefined, ''>;
  namePl?: Yup.StringSchema<string, Yup.AnyObject, undefined, ''>;
  validFrom?: Yup.StringSchema<string, Yup.AnyObject, undefined, ''>;
  duration?: Yup.NumberSchema<number, Yup.AnyObject, undefined, ''>;
};

type TrainingForm = Omit<TrainingDto, 'questionnaire'> & TrainingVersionDto & { versionNote?: string };

export const TrainingDetail: FC = () => {
  const { t, i18n } = useTranslation();
  const { navigate } = useNavigation();
  const { trainingId } = useParams();
  const training = useAppSelector(selectTraining);
  const identityCompanyId = useAppSelector(selectIdentityCompanyId);

  const companyChecksCanCreateTraining = useAppSelector(selectCompanyChecksCanCreateTraining);
  const dispatch = useAppDispatch();
  const companyCustomization = useAppSelector(selectCompanyCustomization);

  useEffect(() => {
    if (trainingId === 'new') {
      dispatch(trainingActions.default());
      dispatch(getCompanyChecks());
    } else {
      dispatch(getTraining(trainingId!));
    }
  }, [trainingId, identityCompanyId, dispatch]);

  const getNameByLanguage = useCallback(
    (lang: string, name?: string) => {
      return companyCustomization?.applicationLanguages.includes(lang) ? name : undefined;
    },
    [companyCustomization],
  );

  const handleSubmit = useCallback(
    async (data: TrainingForm) => {
      //Fix BE validation on editing.
      const duration = trainingId === 'new' ? data.duration : data.duration || 1;

      const trainingData: TrainingDto = {
        name: getNameByLanguage('cs', data.name),
        nameEn: getNameByLanguage('en', data.nameEn),
        nameDe: getNameByLanguage('de', data.nameDe),
        namePl: getNameByLanguage('pl', data.namePl),
        nameRu: getNameByLanguage('ru', data.nameRu),
        state: data.state,
        note: data.note,
        duration,
      };

      const versionData: TrainingVersionDto = {
        validFrom: data.validFrom,
        validTo: data.validTo,
        questionnaire: data.questionnaire,
        duration,
        note: data.versionNote,
      };

      if (trainingId === 'new') {
        // Training creation causes creating a new blank version. Find it and update by data from the form.
        const trainingResponse = await trainingService.create(trainingData, {}, { silentSuccess: true });
        const versionsResponse = await trainingVersionService.query({ trainingId: trainingResponse.data.id! });
        await trainingVersionService.update(versionsResponse.data.content![0].id!, versionData);
      } else if (trainingId) {
        await trainingService.update(trainingId, trainingData);
      }

      dispatch(trainingListActions.reload(true));
      navigate(PathKey.Training);
    },
    [trainingId, getNameByLanguage, dispatch, navigate],
  );

  const validator: Validator = useMemo(() => {
    const returnValidator: Validator = {};

    if (companyCustomization?.applicationLanguages.includes('cs')) {
      returnValidator['name'] = validations.stringRequired(t);
    }
    if (companyCustomization?.applicationLanguages.includes('en')) {
      returnValidator['nameEn'] = validations.stringRequired(t);
    }
    if (companyCustomization?.applicationLanguages.includes('de')) {
      returnValidator['nameDe'] = validations.stringRequired(t);
    }
    if (companyCustomization?.applicationLanguages.includes('pl')) {
      returnValidator['namePl'] = validations.stringRequired(t);
    }
    if (companyCustomization?.applicationLanguages.includes('ru')) {
      returnValidator['nameRu'] = validations.stringRequired(t);
    }
    if (trainingId === 'new') {
      returnValidator['validFrom'] = validations.stringDateNotPastRequired(t);
      returnValidator['duration'] = validations.intPositive(t);
    }
    return returnValidator;
  }, [t, companyCustomization, trainingId]);

  return (
    <>
      <DetailHeader title="training.detailTitle" backTo="/training" />
      <ShowResourceContent
        resource={trainingId === 'new' ? resources.checkOptions : resources.getTrainingById}
        fetchStateSelector={trainingId === 'new' ? [selectTrainingState, selectCompanyChecksState] : [selectTrainingState]}
      >
        <ChangeHistoryTabs history={training.changes} service={trainingService} id={trainingId!}>
          <FormikDebounce<TrainingForm>
            initialValues={{
              ...training,
              ...fixNameInitValue(i18n, training),
              workplaces: training.workplaces?.map((workplace) => ({
                ...workplace,
                establishment: workplace.ref,
              })),
              questionnaire: training.questionnaire?.[0]
                ? {
                    id: '',
                    name: joinStrings2(training.questionnaire!.map((x) => getLangNameObj(i18n, x))),
                  }
                : undefined,
              validFrom: getDateForApi(new Date())!,
            }}
            validationSchema={Yup.object().shape(validator)}
            onSubmit={handleSubmit}
          >
            <Form>
              <FormContainer>
                <InputTextWithLang isNew={trainingId === 'new'} label={t('training.name')} maxLength={50} required />
                <Select name="state" label={t('training.state')}>
                  <QcsMenuItem value={EntityState.Active}>{t('entityState.ACTIVE')}</QcsMenuItem>
                  <QcsMenuItem value={EntityState.Inactive}>{t('entityState.INACTIVE')}</QcsMenuItem>
                </Select>
                <Input name="note" label={t('training.note')} maxLength={250} multiline={true} rows={4} />
                {trainingId !== 'new' && (
                  <WorkplaceAutocomplete multiple name="workplaces" label={t('training.workplaces')} disabled showEstabilishmentName={true} />
                )}
                {trainingId === 'new' && (
                  <FormGroup name="training.firstVersion">
                    <DatePicker name="validFrom" label={t('training.validFrom')} disablePast disabled required />
                    <DatePicker name="validTo" label={t('training.validTo')} disabled />
                    <QuestionnaireAutocomplete name="questionnaire" label={t('training.questionnaire')} />
                    <Input name="duration" label={t('training.duration')} type="number" required />
                    <Input name="versionNote" label={t('training.versionNote')} maxLength={250} multiline={true} rows={4} />
                  </FormGroup>
                )}
                <ShowError
                  resource={[resources.updateTrainingVersion, resources.queryTrainingVersions, resources.updateTraining, resources.createTraining]}
                />
                <SubmitButton disabled={trainingId === 'new' && !companyChecksCanCreateTraining} />
              </FormContainer>
            </Form>
          </FormikDebounce>
          {trainingId !== 'new' && <TrainingVersion />}
        </ChangeHistoryTabs>
      </ShowResourceContent>
    </>
  );
};
